import { useEffect, useMemo, useState } from 'react';
import { Table, Select, Modal } from 'antd';
import { businessDataAPI, sessionAPI, taskFlowAPI } from '@api';
import { $error, LazyLoadingImage, AuthButton } from '@components';
import PreviewImage from '@components/image/preview_image';
import env from 'env';
import {
    notifyBatchProduceScoreSubmit,
    resolveProduceScoreSubmitReason
} from '@/utils/produceScoreSubmitFeedback';
import ProductionUnratedFilterForm from './filterForm';

const defaultPagination = {
    pageSize: 10,
    current: 1
};

const defaultFilters = () => ({
    asset_id_name: '',
    warehouse_stage: 'after_2025'
});

const isLocalEnv = () => String(env.envName || '').toLowerCase() === 'local';

const buildScoreUpdatePayload = (record, score) => {
    const taskId = Number(record?.task_id || 0);
    if (taskId <= 0) {
        throw new Error('缺少任务 ID，无法提交美术评分');
    }
    return {
        id: taskId,
        action: 'update_produce_score',
        data: { score: Number(score) }
    };
};

const getRecordAssetNo = (record) => record?.asset_no || record?.asset_uid || String(record?.task_id || '-');

const submitProduceScore = (payload) => {
    if (isLocalEnv()) {
        return taskFlowAPI.updateProduceScore(payload);
    }
    if (env.localStatsApiUrl) {
        return businessDataAPI.updateProduceUnratedScore(payload);
    }
    return taskFlowAPI.updateProduceScore(payload);
};

const getRowKey = (record) => `task-${record?.task_id || 0}`;

const getDraftScoreKey = (record) => getRowKey(record);

/**
 * 制作统计-未评分数据列表。
 * @returns {JSX.Element}
 */
const ProductionUnrated = () => {
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState(defaultPagination);
    const [submittingScores, setSubmittingScores] = useState(false);
    const [draftScores, setDraftScores] = useState({});
    const [filters, setFilters] = useState(defaultFilters);
    const draftScoreCount = Object.keys(draftScores).length;

    const scoreOptions = useMemo(
        () => Array.from({ length: 10 }, (_, idx) => ({ label: `${idx + 1}`, value: idx + 1 })),
        []
    );

    const buildRequestParams = (currentPage, pageSize, filterOverride) => {
        const activeFilters = filterOverride || filters;
        return {
            page: currentPage,
            page_size: pageSize,
            asset_id_name: (activeFilters.asset_id_name || '').trim(),
            warehouse_stage: activeFilters.warehouse_stage || 'after_2025'
        };
    };

    const fetchList = async (
        currentPage = pagination.current,
        pageSize = pagination.pageSize,
        filterOverride
    ) => {
        const params = buildRequestParams(currentPage, pageSize, filterOverride);
        setLoading(true);
        try {
            const res = await businessDataAPI.getProduceUnratedList(params);
            if (res.code !== 1000) {
                throw new Error(res.msg || '获取未评分数据失败');
            }
            const infos = res.data?.infos || [];
            setList(infos);
            setTotal(res.data?.total || infos.length);
            setDraftScores({});
        } catch (error) {
            setList([]);
            setTotal(0);
            $error(error.message || '获取未评分数据失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList(1, pagination.pageSize);
    }, []);

    const handleSearch = (values) => {
        const nextFilters = {
            asset_id_name: (values?.asset_id_name || '').trim(),
            warehouse_stage: values?.warehouse_stage || 'after_2025'
        };
        setFilters(nextFilters);
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchList(1, pagination.pageSize, nextFilters);
    };

    const handleResetFilters = () => {
        const nextFilters = defaultFilters();
        setFilters(nextFilters);
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchList(1, pagination.pageSize, nextFilters);
    };

    const onPreview = (src) => {
        if (!src) return;
        PreviewImage.open({
            images: [src],
            request: (url) => sessionAPI.fetchOriginalImage(url).then((res) => res.data?.origin_urls?.[0] || '')
        });
    };

    const handleScoreChange = (score, record) => {
        const key = getDraftScoreKey(record);
        if (!key) return;
        setDraftScores((prev) => ({ ...prev, [key]: score }));
    };

    const submitOneDraftScore = async (record, score) => {
        const assetNo = getRecordAssetNo(record);
        try {
            const res = await submitProduceScore(buildScoreUpdatePayload(record, score));
            if (!res || res.code !== 1000) {
                return {
                    ok: false,
                    assetNo,
                    reason: resolveProduceScoreSubmitReason(res, null, `${assetNo} 提交失败`)
                };
            }
            return { ok: true, assetNo };
        } catch (error) {
            return {
                ok: false,
                assetNo,
                reason: resolveProduceScoreSubmitReason(null, error, `${assetNo} 提交失败`)
            };
        }
    };

    const submitDraftScores = async () => {
        const entries = Object.entries(draftScores);
        if (entries.length === 0) {
            return { total: 0, successCount: 0, failCount: 0, failures: [] };
        }
        setSubmittingScores(true);
        const failures = [];
        let successCount = 0;
        try {
            for (const [rowKey, score] of entries) {
                const record = list.find((item) => getDraftScoreKey(item) === rowKey);
                if (!record) continue;
                const outcome = await submitOneDraftScore(record, score);
                if (outcome.ok) {
                    successCount += 1;
                } else {
                    failures.push({ assetNo: outcome.assetNo, reason: outcome.reason });
                }
            }
            const total = successCount + failures.length;
            return { total, successCount, failCount: failures.length, failures };
        } finally {
            setSubmittingScores(false);
        }
    };

    const handleBatchSubmitScores = async () => {
        const result = await submitDraftScores();
        if (result.total === 0) {
            $error('请先选择评分');
            return;
        }
        notifyBatchProduceScoreSubmit(result);
        if (result.successCount > 0) {
            await fetchList(pagination.current, pagination.pageSize);
        }
    };

    const columns = [
        {
            title: '预览图',
            dataIndex: 'preview_image',
            width: 90,
            render: (src) => (
                <LazyLoadingImage
                    preview={{ visible: false }}
                    onClick={(event) => {
                        event.stopPropagation();
                        onPreview(src);
                    }}
                    width="60px"
                    height="60px"
                    src={src}
                />
            )
        },
        {
            title: '资产ID',
            dataIndex: 'asset_no',
            render: (_, record) => record.asset_no || record.asset_uid || '-'
        },
        { title: '名称', dataIndex: 'asset_name' },
        {
            title: '入库时间',
            dataIndex: 'review_time',
            render: (_, record) => record.review_time || record.created_at || '-'
        },
        {
            title: '评分',
            dataIndex: 'score',
            width: 180,
            render: (_, record) => (
                <Select
                    placeholder="请选择评分"
                    options={scoreOptions}
                    style={{ width: 130 }}
                    onChange={(value) => handleScoreChange(value, record)}
                    value={draftScores[getDraftScoreKey(record)]}
                    disabled={submittingScores || !record?.task_id}
                />
            )
        }
    ];

    const onTableChange = (pageInfo) => {
        const next = {
            pageSize: pageInfo.pageSize || pagination.pageSize,
            current: pageInfo.current || pagination.current
        };
        const goNextPage = () => {
            setPagination(next);
            fetchList(next.current, next.pageSize);
        };

        if (draftScoreCount === 0) {
            goNextPage();
            return;
        }

        Modal.confirm({
            title: '当前页有未提交评分',
            content: '请先提交打分再翻页，否则会丢失记录。',
            okText: '提交并翻页',
            cancelText: '直接翻页',
            onOk: async () => {
                const result = await submitDraftScores();
                notifyBatchProduceScoreSubmit(result);
                if (result.failCount > 0) {
                    if (result.successCount > 0) {
                        await fetchList(pagination.current, pagination.pageSize);
                    }
                    return Promise.reject(new Error('部分评分提交失败'));
                }
                if (result.total === 0) {
                    return Promise.reject(new Error('请先选择评分'));
                }
                goNextPage();
                return Promise.resolve();
            },
            onCancel: () => {
                setDraftScores({});
                goNextPage();
            }
        });
    };

    return (
        <div className="page-container">
            <h3 className="mg-b-12">未评分数据</h3>
            <ProductionUnratedFilterForm
                onSearch={handleSearch}
                onReset={handleResetFilters}
            />
            <div className="mg-b-12">
                <AuthButton
                    permission="produce_unrated_score_submit"
                    type="primary"
                    onClick={handleBatchSubmitScores}
                    loading={submittingScores}
                    disabled={submittingScores || draftScoreCount === 0}
                >
                    提交打分
                </AuthButton>
            </div>
            <Table
                rowKey={getRowKey}
                dataSource={list}
                columns={columns}
                loading={loading}
                pagination={{
                    position: ['bottomCenter'],
                    showQuickJumper: true,
                    showSizeChanger: false,
                    total,
                    showTotal: (count) => `共 ${count} 条`,
                    ...pagination
                }}
                onChange={onTableChange}
            />
        </div>
    );
};

export default ProductionUnrated;
